import { Injectable }                    from '@angular/core';
import { NavigationEnd, Router }         from '@angular/router';
import { filter }                        from 'rxjs/operators';

import { environment }  from '../../../environments/environment';
import { AuthUser }     from '@models/user/auth-user';
import { GtmService }   from './gtm.service';
import { UserService }  from './user.service';

// PLURALL_TRACKER: to re-enable, uncomment the import and all marked blocks below,
// then restore `plurallFeedbackId` in environment files and the Environment interface.
// import { PlurallTrackerService } from './plurall-tracker.service';

export type EnvironmentName = 'production' | 'staging' | 'development';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  private lastPushedUuid: string | null = null;

  constructor(
    private router:      Router,
    private gtmService:  GtmService,
    private userService: UserService,
    // PLURALL_TRACKER: private plurallTrackerService: PlurallTrackerService,
  ) {}

  // ---------------------------------------------------------------------------
  // Environment mapping
  // ---------------------------------------------------------------------------

  public static mapEnvironmentName(
    production:     boolean,
    gtmContainerId: string
  ): EnvironmentName {
    if (production) return 'production';
    return gtmContainerId?.trim() ? 'staging' : 'development';
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  public init(): void {
    const envName = AnalyticsService.mapEnvironmentName(
      environment.production,
      environment.gtmContainerId
    );

    this.gtmService.init(environment.gtmContainerId);
    // PLURALL_TRACKER: this.plurallTrackerService.init(envName, environment.plurallFeedbackId);

    this.subscribeToPageViews(envName);
    this.subscribeToUserIdentity(envName);
  }

  // ---------------------------------------------------------------------------
  // Page views
  // ---------------------------------------------------------------------------

  private subscribeToPageViews(envName: EnvironmentName): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects, envName);
    });
  }

  private trackPageView(url: string, envName: EnvironmentName): void {
    // PLURALL_TRACKER: (window as any).PLURALL_TRACKER?.pageView();

    if (envName === 'development') return;

    try {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({ event: 'pageview', pagePath: url });
      }
    } catch (error) {
      console.error('[AnalyticsService] dataLayer.push (pageview) failed:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // User identity
  // ---------------------------------------------------------------------------

  private subscribeToUserIdentity(envName: EnvironmentName): void {
    if (envName === 'development') return;

    this.userService.getActiveUser().pipe(
      filter((user: AuthUser) => !!user)
    ).subscribe((user: AuthUser) => {
      this.trackUserIdentity(user);
    });
  }

  private trackUserIdentity(user: AuthUser): void {
    if (user.uuid === this.lastPushedUuid) return;

    try {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event:     'user_identity',
          userRole:  user.perfil,
          partnerId: user.parceiroId ?? null,
        });
        this.lastPushedUuid = user.uuid;
      }
    } catch (error) {
      console.error('[AnalyticsService] dataLayer.push (user_identity) failed:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Legacy public method — kept for backward compatibility with AppComponent
  // callsite that does: analyticsService.pageView() after scriptReady('gtm')
  // ---------------------------------------------------------------------------

  public pageView(): void {
    const envName = AnalyticsService.mapEnvironmentName(
      environment.production,
      environment.gtmContainerId
    );
    this.trackPageView(this.router.url, envName);
  }
}
